/**
 * pages/AppointmentsPage.tsx — CLI-452
 * Список визитов пациента + отмена. Мобайл-first.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getAppointments, cancelAppointment, type PatientAppointment } from '../services/patient.service';

const STATUS_COLORS: Record<string, string> = {
  SCHEDULED: 'bg-blue-100 text-blue-700',
  CONFIRMED: 'bg-green-100 text-green-700',
  ARRIVED: 'bg-teal-100 text-teal-700',
  COMPLETED: 'bg-gray-100 text-gray-600',
  CANCELLED: 'bg-red-100 text-red-600',
  NO_SHOW: 'bg-orange-100 text-orange-700',
};

const STATUS_RU: Record<string, string> = {
  SCHEDULED: 'Записан', CONFIRMED: 'Подтверждён', ARRIVED: 'Принят',
  COMPLETED: 'Завершён', CANCELLED: 'Отменён', NO_SHOW: 'Неявка',
};

const TYPE_RU: Record<string, string> = {
  consultation: 'Приём', procedure: 'Процедура', lab: 'Сдача анализов',
};

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function isFuture(iso: string): boolean {
  return new Date(iso).getTime() > Date.now();
}

function canCancel(appt: PatientAppointment): boolean {
  if (appt.status !== 'SCHEDULED' && appt.status !== 'CONFIRMED') return false;
  const twoHours = 2 * 60 * 60 * 1000;
  return new Date(appt.date).getTime() - Date.now() > twoHours;
}

export function AppointmentsPage() {
  const qc = useQueryClient();
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const { data: appts, isLoading, isError } = useQuery({
    queryKey: ['patient-appointments'],
    queryFn: getAppointments,
  });

  const cancelMut = useMutation({
    mutationFn: cancelAppointment,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['patient-appointments'] });
      setConfirmId(null);
    },
  });

  if (isLoading) return (
    <div className="p-4 space-y-3">
      {Array.from({ length: 3 }).map((_, i) => <div key={i} className="bg-white rounded-xl h-24 animate-pulse" />)}
    </div>
  );

  if (isError) return <div className="p-4 text-center text-red-500">Ошибка загрузки записей.</div>;

  const upcoming = appts?.filter((a) => isFuture(a.date)) ?? [];
  const past = appts?.filter((a) => !isFuture(a.date)) ?? [];

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-gray-800">Мои записи</h2>
        <Link
          to="/portal/book"
          className="bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium px-4 py-2 rounded-xl transition"
        >
          + Записаться
        </Link>
      </div>

      {/* Предстоящие */}
      {upcoming.length > 0 && (
        <section>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Предстоящие</h3>
          <div className="space-y-2">
            {upcoming.map((appt) => (
              <div key={appt.id} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <div className="flex justify-between items-start gap-2">
                  <div>
                    <p className="font-semibold text-gray-800 text-sm">{TYPE_RU[appt.type] ?? appt.type}</p>
                    <p className="text-xs text-gray-500">{formatDateTime(appt.date)}</p>
                    {appt.room && <p className="text-xs text-gray-400 mt-0.5">Кабинет: {appt.room.name}</p>}
                  </div>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_COLORS[appt.status] ?? 'bg-gray-100 text-gray-500'}`}>
                    {STATUS_RU[appt.status] ?? appt.status}
                  </span>
                </div>
                {appt.reason && <p className="text-xs text-gray-500 mt-2">{appt.reason}</p>}
                {canCancel(appt) && (
                  <button
                    onClick={() => setConfirmId(appt.id)}
                    className="mt-3 text-xs text-red-500 hover:text-red-700 font-medium"
                  >
                    Отменить запись
                  </button>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* История */}
      {past.length > 0 && (
        <section>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">История</h3>
          <div className="space-y-2">
            {past.slice(0, 20).map((appt) => (
              <div key={appt.id} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 opacity-75">
                <div className="flex justify-between items-start gap-2">
                  <div>
                    <p className="font-semibold text-gray-700 text-sm">{TYPE_RU[appt.type] ?? appt.type}</p>
                    <p className="text-xs text-gray-500">{formatDateTime(appt.date)}</p>
                  </div>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_COLORS[appt.status] ?? 'bg-gray-100 text-gray-500'}`}>
                    {STATUS_RU[appt.status] ?? appt.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {!appts?.length && (
        <div className="text-center text-gray-500 mt-12">
          <p className="text-4xl mb-3">📅</p>
          <p className="font-medium text-gray-700">Записей пока нет</p>
          <Link to="/portal/book" className="mt-4 inline-block bg-primary-600 text-white text-sm px-5 py-2 rounded-xl">
            Записаться сейчас
          </Link>
        </div>
      )}

      {/* Confirm dialog */}
      {confirmId && (
        <div className="fixed inset-0 bg-black/50 flex items-end z-50" onClick={() => setConfirmId(null)}>
          <div className="bg-white rounded-t-2xl w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-gray-800 mb-2">Отменить запись?</h3>
            <p className="text-sm text-gray-500 mb-5">Это действие нельзя отменить.</p>
            <div className="flex gap-3">
              <button
                onClick={() => cancelMut.mutate(confirmId)}
                disabled={cancelMut.isPending}
                className="flex-1 bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white font-semibold py-3 rounded-xl"
              >
                {cancelMut.isPending ? 'Отмена…' : 'Да, отменить'}
              </button>
              <button onClick={() => setConfirmId(null)} className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 rounded-xl">
                Назад
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
